import {
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
  type AdminApiPlaybook,
  type PlaybookId,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TeammatePlaybookCoachingManagerInput } from "@repo/app-types/entities/teammatePlaybookCoachingManagerInput";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import type { SimpleTreeSelectOption } from "@repo/ui/Select/simpleTreeSelect";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useMemo, useState, type FC, type ReactNode } from "react";
import {
  usePlaybookTeammateDeleteCoachingManagerInputMutation,
  usePlaybookTeammateSaveCoachingManagerInputMutation,
} from "../../../api/entities/performancePlaybook.js";
import { GradingContextInfoTrigger } from "../../../components/assessment/gradingContextInfoTrigger.js";
import { useCoachingAnalytics } from "../coachingAnalytics.js";
import {
  NextWeekGoalCardShell,
  NextWeekGoalEditor,
  NextWeekGoalEmptyState,
  NextWeekGoalSaved,
} from "./nextWeekGoalCard.js";

export interface NextWeekGoalCardContainerProps {
  playbookId: PlaybookId;
  /** The playbook resolved by the coaching section; undefined while loading. */
  playbook: AdminApiPlaybook | undefined;
  teammateId: TeammateId;
  /** The upcoming coaching period the goal applies to. */
  upcomingPeriodId: string;
  /** Human-readable range of the upcoming goal week ("Thu Jul 09 – Thu Jul 16"). */
  upcomingPeriodLabel: string;
  /** The manager input stored for the upcoming period, if any. */
  managerGoal: TeammatePlaybookCoachingManagerInput | undefined;
  teammateFirstName: string;
  /** Right-aligned header controls (period selector + more menu); hidden while editing. */
  headerControls: ReactNode;
  /** Whether the goal editor is open (owned by the coaching section). */
  isEditing: boolean;
  onOpenEditor: () => void;
  onCloseEditor: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}

/**
 * The "next week" coaching view for managers: the saved manager-set goal, the
 * goal editor, or an empty state inviting the manager to set one.
 */
export const NextWeekGoalCardContainer: FC<NextWeekGoalCardContainerProps> = (
  props,
) => {
  const {
    playbookId,
    playbook,
    teammateId,
    upcomingPeriodId,
    upcomingPeriodLabel,
    managerGoal,
    teammateFirstName,
    headerControls,
    isEditing,
    onOpenEditor,
    onCloseEditor,
    onDirtyChange,
  } = props;

  const { captureManagerInputSave, captureManagerInputDelete } =
    useCoachingAnalytics();
  const [
    isUseAutomaticGoalConfirmationOpen,
    setIsUseAutomaticGoalConfirmationOpen,
  ] = useState(false);

  const [saveGoal, { isLoading: isSaving }] =
    usePlaybookTeammateSaveCoachingManagerInputMutation();
  const [deleteGoal] = usePlaybookTeammateDeleteCoachingManagerInputMutation();

  // The saved goal only counts once it carries a criterion; a bare note row
  // (this period's note left over after "Let Alpharun choose") is not a goal.
  const savedGoal = managerGoal?.criterion_id ? managerGoal : undefined;

  const { criterionOptions, disabledCriterionValues, criteriaById } = useMemo(
    () => buildCriterionOptions(playbook?.assessment_tree.nodes ?? []),
    [playbook?.assessment_tree.nodes],
  );

  const savedCriterion = savedGoal?.criterion_id
    ? criteriaById.get(savedGoal.criterion_id)
    : undefined;

  const handleSave = async (criterionId: string, note: string) => {
    const isEdit = savedGoal !== undefined;

    try {
      await saveGoal({
        playbookId,
        teammateId,
        periodId: upcomingPeriodId,
        criterionId,
        note: note || undefined,
      }).unwrap();
    } catch {
      showToast({
        title: "The goal could not be saved. Please try again.",
        variant: ToastVariant.Error,
      });
      return;
    }

    onCloseEditor();
    captureManagerInputSave({
      inputType: "next_goal",
      isEdit,
      criterionId,
    });
  };

  const handleUseAutomaticGoal = async () => {
    try {
      await deleteGoal({
        playbookId,
        teammateId,
        periodId: upcomingPeriodId,
      }).unwrap();
    } catch {
      showToast({
        title: "The goal could not be removed. Please try again.",
        variant: ToastVariant.Error,
      });
      return;
    }

    onCloseEditor();
    captureManagerInputDelete({ inputType: "next_goal" });
  };

  if (!playbook) {
    return (
      <div className="flex-auto p-3">
        <TextSkeleton />
      </div>
    );
  }

  if (isEditing) {
    return (
      <NextWeekGoalCardShell
        title={savedGoal ? "Edit next week's goal" : "Set next week's goal"}
        subtitle={`Applies to ${upcomingPeriodLabel}`}
      >
        <NextWeekGoalEditor
          teammateFirstName={teammateFirstName}
          criterionOptions={criterionOptions}
          disabledCriterionValues={disabledCriterionValues}
          initialCriterionId={savedGoal?.criterion_id ?? undefined}
          initialNote={savedGoal?.note ?? ""}
          onUseAutomaticGoal={
            savedGoal
              ? () => setIsUseAutomaticGoalConfirmationOpen(true)
              : undefined
          }
          onCancel={onCloseEditor}
          onSave={(criterionId, note) => void handleSave(criterionId, note)}
          isSaving={isSaving}
          onDirtyChange={onDirtyChange}
        />

        <ConfirmationModal
          isOpen={isUseAutomaticGoalConfirmationOpen}
          onOpenChange={setIsUseAutomaticGoalConfirmationOpen}
          title="Let Alpharun choose next week's goal?"
          confirmText="Let Alpharun choose"
          onConfirm={() => void handleUseAutomaticGoal()}
        >
          This will remove the goal you set. Alpharun will pick the goal and
          score target when next week starts.
        </ConfirmationModal>
      </NextWeekGoalCardShell>
    );
  }

  return (
    <NextWeekGoalCardShell
      title="Weekly coaching goal"
      headerControls={headerControls}
    >
      {savedGoal ? (
        <NextWeekGoalSaved
          criterionName={savedCriterion?.name ?? "Unknown criterion"}
          criterionInfoControl={
            savedCriterion?.infoContent ? (
              <GradingContextInfoTrigger
                title={savedCriterion.name}
                content={savedCriterion.infoContent}
              />
            ) : undefined
          }
          note={savedGoal.note}
        />
      ) : (
        <NextWeekGoalEmptyState
          teammateFirstName={teammateFirstName}
          onSetGoal={onOpenEditor}
        />
      )}
    </NextWeekGoalCardShell>
  );
};

interface CriterionOptionDetails {
  name: string;
  infoContent: string | undefined;
}

/**
 * Builds the criterion tree-select options from the playbook assessment tree:
 * sections group their criteria but can't be picked themselves.
 */
function buildCriterionOptions(nodes: readonly PlaybookNode[]): {
  criterionOptions: SimpleTreeSelectOption<string>[];
  disabledCriterionValues: string[];
  criteriaById: Map<string, CriterionOptionDetails>;
} {
  const disabledCriterionValues: string[] = [];
  const criteriaById = new Map<string, CriterionOptionDetails>();

  const buildOptions = (
    currentNodes: readonly PlaybookNode[],
  ): SimpleTreeSelectOption<string>[] =>
    currentNodes.flatMap((node): SimpleTreeSelectOption<string>[] => {
      if (isPlaybookSectionNode(node)) {
        const children = buildOptions(node.nodes);
        // Skip sections with no criteria beneath them.
        if (children.length === 0) {
          return [];
        }
        disabledCriterionValues.push(node.id);
        return [{ value: node.id, label: node.name, children }];
      }

      if (isPlaybookCriterionNode(node)) {
        criteriaById.set(node.id, {
          name: node.name,
          infoContent: node.grading_context || node.description,
        });
        return [{ value: node.id, label: node.name }];
      }

      return [];
    });

  return {
    criterionOptions: buildOptions(nodes),
    disabledCriterionValues,
    criteriaById,
  };
}
